import React, { useEffect, useState } from 'react'
import { ArrowDown2 } from 'iconsax-react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css } from 'styled-components'

import { useMount } from 'utils/hooks'

import {
  ALL_RISK_LEVEL_VALUE,
  GET_SIMULATIONS_META_ASYNC,
  SET_STATUS_SELECTED_FILTERS,
  SIMULATION_STATUSES_VALUES,
} from 'containers/SpearPhishingAgent/constants'
import {
  selectSimulationData,
  selectStatusCountsData,
} from 'containers/SpearPhishingAgent/selectors'

import Button, { SIZES, VARIANTS } from 'components/lib/DesignSystem/V3/Button'
import Combobox from 'components/lib/DesignSystem/V3/Combobox'
import DateRangePicker, { formatDateRange } from 'components/lib/DesignSystem/V3/DateRangePicker'
import stripTransientProps from 'components/lib/DesignSystem/V3/stripTransientProps'

import SelectionBar from './SelectionBar'

import { Popover } from '@base-ui/react/popover'

const Counter = styled.div`
  min-width: 20px;
  height: 20px;
  border-radius: ${({ $rounded }) =>
    $rounded ? 'var(--t-border-radius-rounded)' : 'var(--t-border-radius-md)'};
  background-color: ${({ $active }) =>
    $active ? 'var(--psima-counter-active-bg)' : 'var(--psima-counter-inactive-bg)'};
  color: ${({ $active }) =>
    $active ? 'var(--psima-counter-active-text)' : 'var(--psima-counter-inactive-text)'};
  font-size: var(--t-typography-font-size-xs);
  font-weight: var(--t-font-weight-semibold);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--t-spacing-0-5) var(--t-spacing-1-5);
`

// Override V3 Button's TERTIARY+floating (hardcoded white bg / blue-50 border) and
// SPECIAL (text uses var(--white) which resolves to #1a1d24 in dark) so the
// filter chips render correctly in both themes.
const filterChipInteractiveShadow = (shadowToken) => css`
  & > button:not(:disabled):hover > span,
  & > button:not(:disabled):focus > span,
  & > button:not(:disabled):focus-visible > span,
  & > button:not(:disabled):focus-within > span,
  & > button:not(:disabled):active > span {
    box-shadow: ${shadowToken};
  }

  & > button:not(:disabled):focus-visible > span {
    box-shadow:
      ${shadowToken},
      0 0 0 3px var(--input-focus-ring-default);
  }
`

const FilterButtonWrapper = styled.span`
  ${({ $selected }) =>
    $selected
      ? css`
          & > button > span {
            color: var(--psima-filter-chip-selected-text);
            background: var(--psima-filter-chip-selected-bg);
            border-color: var(--psima-filter-chip-selected-border);
            box-shadow: var(--psima-filter-chip-selected-shadow);
          }
          & > button:not(:disabled):hover > span,
          & > button:not(:disabled):focus > span,
          & > button:not(:disabled):focus-visible > span,
          & > button:not(:disabled):focus-within > span,
          & > button:not(:disabled):active > span {
            background: var(--psima-filter-chip-selected-bg-hover);
            border-color: var(--psima-filter-chip-selected-border);
            color: var(--psima-filter-chip-selected-text);
          }
          ${filterChipInteractiveShadow('var(--psima-filter-chip-selected-shadow)')}
        `
      : css`
          & > button > span {
            background: var(--psima-filter-chip-bg);
            border-color: var(--psima-filter-chip-border);
            color: var(--psima-filter-chip-text);
            box-shadow: var(--psima-filter-chip-shadow);
          }
          & > button:not(:disabled):hover > span,
          & > button:not(:disabled):focus > span,
          & > button:not(:disabled):focus-visible > span,
          & > button:not(:disabled):focus-within > span,
          & > button:not(:disabled):active > span {
            background: var(--psima-filter-chip-bg-hover);
            border-color: var(--psima-filter-chip-border-hover);
            color: var(--psima-filter-chip-text);
          }
          ${filterChipInteractiveShadow('var(--psima-filter-chip-shadow)')}
        `}
`

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--t-spacing-3);
  margin-bottom: var(--t-spacing-6);
  flex-wrap: wrap;
`

/* Department is a filter, so it lives with the status chips on the left; only the
   sort control sits on the right. */
const LeftControls = styled.div`
  display: flex;
  align-items: center;
  gap: var(--t-spacing-3);
  flex-wrap: wrap;
`

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: var(--t-spacing-3);
`

const DepartmentFilter = styled.div`
  min-width: 210px;
`

/* Mirrors the V3 Combobox trigger (medium size, default status) so the two dropdowns
   above the table read as one kind of control. Those helpers are private to Combobox,
   hence the copy; keep it in step with getSizeStyles / getStatusStyles there. */
const DateTrigger = styled(stripTransientProps(Popover.Trigger))`
  display: inline-flex;
  align-items: center;
  gap: var(--t-spacing-2);
  min-height: 36px;
  min-width: 200px;
  padding: 0 var(--t-spacing-3-5);
  border: 1px solid
    ${({ $hasValue }) => ($hasValue ? 'var(--primary)' : 'var(--input-border-color)')};
  border-radius: var(--border-radius-rounded-field, var(--t-border-radius-md));
  background: var(--color-surface);
  color: ${({ $hasValue }) => ($hasValue ? 'var(--color-text)' : 'var(--hint-color)')};
  font-family: var(--t-typography-font-family-font-family-default);
  font-size: var(--t-typography-font-size-sm);
  font-weight: var(--t-typography-font-weight-regular);
  line-height: 1.2;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    border-color: var(--primary);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--input-focus-ring-default);
  }

  > svg {
    margin-left: auto;
    flex-shrink: 0;
    color: var(--primary);
  }
`

/* Portaled to <body>: --z-index-dropdown keeps it above the sticky table and under
   the app header, same as the V3 Combobox beside it. */
const DatePositioner = styled(Popover.Positioner)`
  z-index: var(--z-index-dropdown);
`

const DatePopup = styled(Popover.Popup)`
  border-radius: var(--t-border-radius-lg);
  background: var(--color-elevated);
  border: 1px solid var(--card-border-color);
  box-shadow: var(--shadow-2, var(--shadow-1));
`

const STATUS_CHIPS = [
  { value: SIMULATION_STATUSES_VALUES.COMPLETED, label: 'Completed' },
  { value: SIMULATION_STATUSES_VALUES.ACTIVE, label: 'Active' },
  { value: SIMULATION_STATUSES_VALUES.SENDING, label: 'Sending' },
]

const FiltersSection = ({
  countsUnavailable = false,
  dateRange,
  onDateRangeChange,
  departmentOptions = [],
  selectedDepartments = [],
  onDepartmentsChange,
  selectedCount = 0,
  totalCount = 0,
  onSelectAllMatching,
  onDownloadCsv,
  onClearSelection,
}) => {
  const dispatch = useDispatch()
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
  const statusCountsData = useSelector(selectStatusCountsData)
  const { statusSelectedFilters } = useSelector(selectSimulationData)
  const simulations = {
    total: statusCountsData.data?.simulations?.total ?? 0,
    byStatus: statusCountsData.data?.simulations?.byStatus ?? {},
  }

  /** Each status is its own chip now — Active and Sending toggle independently. */
  const onStatusClick = (status) => {
    const isOnlyAllSelected =
      statusSelectedFilters.length === 1 && statusSelectedFilters[0] === ALL_RISK_LEVEL_VALUE

    if (isOnlyAllSelected && status === ALL_RISK_LEVEL_VALUE) return

    let newStatusSelectedFilters = [...statusSelectedFilters]

    if (status === ALL_RISK_LEVEL_VALUE) {
      newStatusSelectedFilters = [ALL_RISK_LEVEL_VALUE]
    } else if (isOnlyAllSelected) {
      newStatusSelectedFilters = [status]
    } else if (statusSelectedFilters.includes(status)) {
      newStatusSelectedFilters = newStatusSelectedFilters.filter(
        (selectedStatus) => selectedStatus !== status
      )
    } else {
      newStatusSelectedFilters.push(status)
    }

    dispatch({
      type: SET_STATUS_SELECTED_FILTERS,
      payload: { value: newStatusSelectedFilters },
    })
  }

  useMount(() => {
    dispatch(GET_SIMULATIONS_META_ASYNC.initiate())
  })

  useEffect(() => {
    if (!statusSelectedFilters.length) {
      dispatch({
        type: SET_STATUS_SELECTED_FILTERS,
        payload: { value: [ALL_RISK_LEVEL_VALUE] },
      })
    }
  }, [statusSelectedFilters])

  const isAllSelected = statusSelectedFilters.includes(ALL_RISK_LEVEL_VALUE)

  return (
    <>
      <ControlsRow>
        <LeftControls>
          <FilterButtonWrapper $selected={isAllSelected}>
            <Button
              id="Psima_Launched_Status_Button-All"
              dataTestId="Psima_Launched_Status_Button-All"
              ariaPressed={isAllSelected}
              size={SIZES.MEDIUM}
              floating
              variant={isAllSelected ? VARIANTS.SPECIAL : VARIANTS.TERTIARY}
              rightElement={
                <Counter
                  data-testid="Psima_Launched_Status_Counter-All"
                  $rounded={simulations.total.toString().length < 9}
                  $active={isAllSelected}
                >
                  {countsUnavailable ? '–' : simulations.total}
                </Counter>
              }
              onClick={() => onStatusClick(ALL_RISK_LEVEL_VALUE)}
              disabled={simulations.total === 0}
            >
              All launched
            </Button>
          </FilterButtonWrapper>
          {STATUS_CHIPS.map(({ value, label }) => {
            const isSelected = statusSelectedFilters.includes(value)
            const count = simulations.byStatus[value] ?? 0

            return (
              <FilterButtonWrapper key={value} $selected={isSelected}>
                <Button
                  id={`Psima_Launched_Status_Button-${label}`}
                  dataTestId={`Psima_Launched_Status_Button-${label}`}
                  ariaPressed={isSelected}
                  size={SIZES.MEDIUM}
                  floating
                  variant={isSelected ? VARIANTS.SPECIAL : VARIANTS.TERTIARY}
                  rightElement={
                    <Counter
                      data-testid={`Psima_Launched_Status_Counter-${label}`}
                      $rounded={count.toString().length < 9}
                      $active={isSelected}
                    >
                      {countsUnavailable ? '–' : count}
                    </Counter>
                  }
                  onClick={() => onStatusClick(value)}
                  disabled={countsUnavailable || count === 0}
                >
                  {label}
                </Button>
              </FilterButtonWrapper>
            )
          })}
          <Popover.Root open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
            <DateTrigger
              type="button"
              id="Psima_Launched_DateWindow_Button"
              data-testid="Psima_Launched_DateWindow_Button"
              $hasValue={Boolean(dateRange?.startDate)}
            >
              {dateRange?.startDate ? formatDateRange(dateRange) : 'Launch date range'}
              <ArrowDown2 size={16} color="currentColor" />
            </DateTrigger>
            <Popover.Portal>
              <DatePositioner side="bottom" align="start" sideOffset={8}>
                <DatePopup data-testid="Psima_Launched_DateWindow_Popover">
                  <DateRangePicker
                    value={dateRange}
                    onApply={(range) => {
                      onDateRangeChange(range)
                      setIsDatePickerOpen(false)
                    }}
                    onCancel={() => setIsDatePickerOpen(false)}
                  />
                </DatePopup>
              </DatePositioner>
            </Popover.Portal>
          </Popover.Root>
          <DepartmentFilter>
            <Combobox
              id="Psima_Launched_Department_Combobox"
              dataTestId="Psima_Launched_Department_Combobox"
              multiple
              selectAllLabel="All departments"
              placeholder="Department"
              options={departmentOptions}
              value={selectedDepartments}
              onChange={onDepartmentsChange}
              emptyText="No departments yet"
            />
          </DepartmentFilter>
        </LeftControls>
        <RightControls />
      </ControlsRow>
      {selectedCount > 0 && (
        <SelectionBar
          selectedCount={selectedCount}
          totalCount={totalCount}
          allMatchingSelected={selectedCount >= totalCount}
          onSelectAllMatching={onSelectAllMatching}
          onClear={onClearSelection}
          onDownloadCsv={onDownloadCsv}
        />
      )}
    </>
  )
}

export default FiltersSection
